import Seo from "@/components/Seo";
import { Link } from "react-router-dom";
import { LEGAL } from "@/data/site";
import PageHero from "@/components/PageHero";
import LeadForm from "@/components/LeadForm";
import { Reveal, Label } from "@/components/ui-bits";

export default function ContactPage() {
  return (
    <>
      <Seo
        title="Contact & Complaints | Androgen Health"
        description="Start your assessment or reach the Androgen Health team. Clinical queries are routed to trained staff; complaints are handled separately from clinical care."
      />
      <PageHero
        label="Contact & complaints"
        title="Ask a question first."
        lede="Tell us about your situation and we'll explain your assessment options and their exact cost — before anything is booked, and with no treatment promised. Ready to go ahead? Book directly instead."
        safety={false}
        meta={
          <Link to="/book" className="btn-primary !py-2.5 !px-5 text-sm" data-testid="contact-book-link">
            Book an assessment
          </Link>
        }
      />

      <section className="pb-24 px-5 sm:px-8" data-testid="contact-body">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-10">
            <Reveal>
              <div className="card-line rounded-2xl p-8">
                <Label>What happens next</Label>
                <ol className="mt-6 space-y-5">
                  {[
                    "We review your enquiry and come back with the right assessment pathway.",
                    "You receive the exact cost of each component before booking anything.",
                    "Assessment proceeds — history, blood tests, clinical consultation.",
                    "You get a clear answer, including 'TRT is not appropriate for you' when true.",
                  ].map((s, i) => (
                    <li key={s} className="flex gap-4">
                      <span className="font-mono text-teal text-sm pt-0.5">{String(i + 1).padStart(2, "0")}</span>
                      <p className="text-bone/85 text-sm leading-relaxed">{s}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>

            <Reveal>
              <div className="card-line rounded-2xl p-8">
                <Label>Complaints & escalation</Label>
                <div className="text-mute text-sm leading-relaxed mt-5 space-y-4">
                  <p>
                    Complaints are handled separately from clinical care, acknowledged promptly and
                    investigated through our clinical governance procedure. You can raise a
                    complaint through this page's enquiry form — mark it "complaint" — and it will
                    be routed away from clinical staff to the governance lead.
                  </p>
                  <p>
                    For urgent medical concerns during treatment, contact 111 (or 999 in an
                    emergency). Suspected serious adverse effects are escalated through our
                    documented protocols and should not wait for a routine reply.
                  </p>
                  <p className="text-dusk text-xs" data-testid="contact-provider">
                    Provider: {LEGAL.provider}, Companies House {LEGAL.companiesHouse}. CQC-registered private
                    clinic at {LEGAL.address}. ICO registration {LEGAL.ico}. Unresolved complaints about
                    independent healthcare can be escalated to ISCAS or the CQC.
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal>
              <p className="text-xs text-dusk leading-relaxed">
                Clinical queries are routed to appropriately trained staff, and administrative
                support is kept separate from clinical advice. Response expectations are confirmed
                to you in writing at first contact.
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <LeadForm defaultInterest="A question before I book" />
          </Reveal>
        </div>
      </section>
    </>
  );
}
